/**
 * (owner, Sep 23: "house roofs with depth like the cars have — gambrel, mansard, saltbox, pyramid hip, flat, shed… attach the type to the
 * building so it could be rendered like those"; "just a few simple ones, and the flat stays as an option") A building's roof as a solid:
 * the faces of a pitched roof over its footprint, heights in metres above the eaves (the top of the walls). Content gives the type
 * (`roofForm` on a building); the renderer lifts each face the way it lifts a roof, skips the ones turned away from the eye, and shades
 * the rest by the sun. Every form here is convex, so no face ever hides part of another that is turned towards the eye — nothing to sort.
 * Pure functions: the geometry has a test (tests/roofs.test.ts) and the renderer stays a renderer.
 */
export type RoofType = 'flat' | 'gable' | 'hip' | 'pyramid' | 'shed' | 'gambrel' | 'mansard' | 'saltbox';
export const ROOF_TYPES: RoofType[] = ['flat', 'gable', 'hip', 'pyramid', 'shed', 'gambrel', 'mansard', 'saltbox'];
/** content: the roof's type, how steep (rise over half the span; a shed: over the whole span), and its colour (default: the roof sprite's base) */
export interface RoofForm { type: RoofType; pitch?: number; color?: string }

export type P3 = [number, number, number];
/** a face: its corners in order round it (outward normal by the right hand), whether it is a wall (the gable ends) and its outward normal */
export interface RoofFace { pts: P3[]; wall: boolean; n: P3 }
export interface RoofGeom { faces: RoofFace[]; rise: number }

const PITCH: Record<RoofType, number> = { flat: 0, gable: 0.62, hip: 0.5, pyramid: 0.55, shed: 0.22, gambrel: 1, mansard: 0.5, saltbox: 0.6 };
/** the tallest a roof gets above its eaves (a long barn would otherwise tower) */
export const MAX_RISE = 5;

const sub = (a: P3, b: P3): P3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: P3, b: P3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
/** Newell's normal of a polygon, normalised */
function normalOf(pts: P3[]): P3 {
  let x = 0, y = 0, z = 0;
  for (let i = 0; i < pts.length; i++) { const a = pts[i], b = pts[(i + 1) % pts.length]; x += (a[1] - b[1]) * (a[2] + b[2]); y += (a[2] - b[2]) * (a[0] + b[0]); z += (a[0] - b[0]) * (a[1] + b[1]); }
  const l = Math.hypot(x, y, z) || 1; return [x / l, y / l, z / l];
}
/** drop repeated corners (a hip on a square footprint closes its ridge to a point) */
function dedupe(pts: P3[]): P3[] { const out: P3[] = []; for (const p of pts) { const q = out[out.length - 1]; if (!q || Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]) > 1e-6) out.push(p); } while (out.length > 1 && Math.hypot(out[0][0] - out[out.length - 1][0], out[0][1] - out[out.length - 1][1], out[0][2] - out[out.length - 1][2]) <= 1e-6) out.pop(); return out; }

/**
 * The roof over a footprint `fw × fh` (tiles, the building's own frame before any turn: x along the frontage, y from the back to the
 * front, which is y = fh). The ridge runs along the longer side (along the frontage when square). Flat → no faces.
 */
export function roofLocal(form: RoofForm, fw: number, fh: number): RoofGeom {
  const type = form.type; if (type === 'flat') return { faces: [], rise: 0 };
  const alongX = fw >= fh, L = alongX ? fw : fh, S = alongX ? fh : fw, pitch = form.pitch ?? PITCH[type];
  const P = (u: number, v: number, z: number): P3 => (alongX ? [u, v, z] : [v, u, z]);
  const quads: { pts: P3[]; wall: boolean }[] = [];
  const face = (wall: boolean, ...pts: P3[]) => quads.push({ pts, wall });
  let R = Math.min(MAX_RISE, pitch * S / 2);
  if (type === 'gable' || type === 'saltbox') {
    const rv = type === 'saltbox' ? S * 0.64 : S / 2; // (a saltbox: the ridge nearer the front, a long slope down the back)
    face(false, P(0, 0, 0), P(L, 0, 0), P(L, rv, R), P(0, rv, R));
    face(false, P(0, rv, R), P(L, rv, R), P(L, S, 0), P(0, S, 0));
    face(true, P(0, 0, 0), P(0, rv, R), P(0, S, 0));
    face(true, P(L, 0, 0), P(L, S, 0), P(L, rv, R));
  } else if (type === 'hip' || type === 'pyramid') {
    const d = type === 'pyramid' ? L / 2 : Math.min(S / 2, L / 2);
    if (type === 'pyramid') R = Math.min(MAX_RISE, pitch * Math.min(L, S) / 2);
    face(false, P(0, 0, 0), P(L, 0, 0), P(L - d, S / 2, R), P(d, S / 2, R));
    face(false, P(0, S, 0), P(d, S / 2, R), P(L - d, S / 2, R), P(L, S, 0));
    face(false, P(0, 0, 0), P(d, S / 2, R), P(0, S, 0));
    face(false, P(L, 0, 0), P(L, S, 0), P(L - d, S / 2, R));
  } else if (type === 'shed') {
    R = Math.min(MAX_RISE, pitch * S); // (high along the back, down to the front)
    face(false, P(0, 0, R), P(L, 0, R), P(L, S, 0), P(0, S, 0));
    face(true, P(0, 0, 0), P(0, 0, R), P(L, 0, R), P(L, 0, 0));
    face(true, P(0, 0, 0), P(0, S, 0), P(0, 0, R));
    face(true, P(L, 0, 0), P(L, 0, R), P(L, S, 0));
  } else if (type === 'gambrel') {
    const a = S * 0.14, zk = R * 0.62; // (the barn's knee: a steep lower slope, a shallow upper one)
    face(false, P(0, 0, 0), P(L, 0, 0), P(L, a, zk), P(0, a, zk));
    face(false, P(0, a, zk), P(L, a, zk), P(L, S / 2, R), P(0, S / 2, R));
    face(false, P(0, S / 2, R), P(L, S / 2, R), P(L, S - a, zk), P(0, S - a, zk));
    face(false, P(0, S - a, zk), P(L, S - a, zk), P(L, S, 0), P(0, S, 0));
    face(true, P(0, 0, 0), P(0, a, zk), P(0, S / 2, R), P(0, S - a, zk), P(0, S, 0));
    face(true, P(L, 0, 0), P(L, S, 0), P(L, S - a, zk), P(L, S / 2, R), P(L, a, zk));
  } else if (type === 'mansard') {
    const m = Math.min(S, L) * 0.16; R = Math.min(MAX_RISE, Math.max(1.6, pitch * 4)); // (a steep skirt all round, a flat top)
    face(false, P(0, 0, 0), P(L, 0, 0), P(L - m, m, R), P(m, m, R));
    face(false, P(L, 0, 0), P(L, S, 0), P(L - m, S - m, R), P(L - m, m, R));
    face(false, P(L, S, 0), P(0, S, 0), P(m, S - m, R), P(L - m, S - m, R));
    face(false, P(0, S, 0), P(0, 0, 0), P(m, m, R), P(m, S - m, R));
    face(false, P(m, m, R), P(L - m, m, R), P(L - m, S - m, R), P(m, S - m, R));
  }
  const C: P3 = [fw / 2, fh / 2, R * 0.35];
  const faces: RoofFace[] = [];
  for (const q of quads) {
    let pts = dedupe(q.pts); if (pts.length < 3) continue;
    let n = normalOf(pts);
    const c: P3 = [0, 0, 0]; for (const p of pts) { c[0] += p[0] / pts.length; c[1] += p[1] / pts.length; c[2] += p[2] / pts.length; }
    if (dot(n, sub(c, C)) < 0) { pts = pts.slice().reverse(); n = [-n[0], -n[1], -n[2]]; }
    faces.push({ pts, wall: q.wall, n });
  }
  return { faces, rise: R };
}

/** a point of the building's own frame (footprint fw × fh) on the world, turned `rot` quarter turns clockwise as the roof sprite is */
export function turnPoint(x: number, y: number, fw: number, fh: number, rot: number): [number, number] {
  return rot === 1 ? [fh - y, x] : rot === 2 ? [fw - x, fh - y] : rot === 3 ? [y, fw - x] : [x, y];
}
const turnVec = (x: number, y: number, rot: number): [number, number] => (rot === 1 ? [-y, x] : rot === 2 ? [-x, -y] : rot === 3 ? [y, -x] : [x, y]);

/** the roof on the world: the local faces turned and moved to the building's corner (x, y) */
export function roofWorld(g: RoofGeom, x: number, y: number, fw: number, fh: number, rot: number): RoofGeom {
  return { rise: g.rise, faces: g.faces.map((f) => ({ wall: f.wall, pts: f.pts.map(([px, py, pz]) => { const [wx, wy] = turnPoint(px, py, fw, fh, rot); return [x + wx, y + wy, pz] as P3; }), n: (() => { const [nx, ny] = turnVec(f.n[0], f.n[1], rot); return [nx, ny, f.n[2]] as P3; })() })) };
}

/** the sun for the roofs (from the south-east, high — the walls' east and south faces are the lit ones too): 0.42 facing away … 1.14 facing it — as strong as the painted roofs' two tones */
const SUN_DIR: P3 = (() => { const v: P3 = [0.35, 0.45, 0.82], l = Math.hypot(...v); return [v[0] / l, v[1] / l, v[2] / l]; })();
export function roofLight(n: P3): number { return 0.42 + 0.72 * Math.max(0, dot(n, SUN_DIR)); }

/** the height of the roof (above the eaves) over a point of the world, 0 off it — for what stands on a roof (a turret) */
export function roofHeightAt(g: RoofGeom, x: number, y: number): number { return roofTopAt(g, x, y).z; }
/** the highest face over a point (its index, -1 off the roof) and its height there */
export function roofTopAt(g: RoofGeom, x: number, y: number): { z: number; face: number } {
  let best = 0, face = -1;
  for (const [fi, f] of g.faces.entries()) {
    if (f.wall || f.n[2] <= 1e-6) continue;
    let inside = false; const p = f.pts;
    for (let i = 0, j = p.length - 1; i < p.length; j = i++) if ((p[i][1] > y) !== (p[j][1] > y) && x < ((p[j][0] - p[i][0]) * (y - p[i][1])) / (p[j][1] - p[i][1]) + p[i][0]) inside = !inside;
    if (!inside) continue;
    const z = p[0][2] - (f.n[0] * (x - p[0][0]) + f.n[1] * (y - p[0][1])) / f.n[2]; if (z > best || face < 0) { best = z; face = fi; }
  }
  return { z: best, face };
}
